import { useMemo } from 'react';
import { Menu, MenuItem, Box, Button, Tooltip } from '@mui/material';
import { styled } from '@mui/system';
import { InfoOutlined } from '@mui/icons-material';
import { snoozeDateFormatter } from '../../../../utils/formatter';
import { useViewMode } from '../../../../hooks/use-view-mode';
import { useSettings } from '../../../../hooks/use-settings';

const StyledMenu = styled(Menu)<{ viewMode: string }>(({ viewMode }) => ({
  zIndex: 10000,
  ...(viewMode !== 'Phone' && {
    marginLeft: '-28px',
    marginTop: '-16px'
  })
}));

const StyledBox = styled(Box)(({ theme }) => ({
  fontFamily: 'Roboto',
  fontSize: 16,
  fontWeight: 400,
  margin: '8px 32px',
  lineHeight: '40px',
  color: '#2C3542',
  '& hr': {
    border: 'none',
    backgroundColor: '#E1E4E7',
    height: '2px',
    margin: '16px 0'
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginLeft: '-8px',
  textTransform: 'initial',
  color: '#0675DD',
  fontWeight: 400,
  fontSize: 16
}));

const StyledTitle = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  cursor: 'initial',
  '&:hover': {
    background: 'transparent'
  }
}));

const StyledTooltip = styled(Tooltip)(({ theme }) => ({
  maxWidth: '210px',
  fontWeight: 500,
  textAlign: 'center'
}));

interface SnoozeMenuProps {
  anchor: null | HTMLElement;
  onClose?: () => void;
}

export default function SnoozeMenu({ anchor, onClose }: SnoozeMenuProps) {
  const { viewMode } = useViewMode();
  const { snooze, snoozeExpirationDate } = useSettings();

  const handleSnooze = (expirationDate?: number) => {
    return () => {
      onClose?.();
      setTimeout(() => {
        snooze(expirationDate);
      }, 500);
    };
  };

  return (
    <StyledMenu
      viewMode={viewMode}
      id='notifications-menu'
      anchorEl={anchor}
      keepMounted={false}
      open={Boolean(anchor)}
      autoFocus={false}
      onClose={onClose}
      variant='selectedMenu'
      anchorOrigin={{
        vertical: 'top',
        horizontal: viewMode !== 'Phone' ? 'left' : 'center'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: viewMode !== 'Phone' ? 'right' : 'center'
      }}
      anchorReference='anchorEl'
    >
      {snoozeExpirationDate ? (
        <StyledBox>
          Do Not Disturb is active...
          <br />
          until {snoozeDateFormatter(snoozeExpirationDate)}
          <hr />
          <StyledButton color='primary' onClick={handleSnooze()}>
            Resume Notifications
          </StyledButton>
        </StyledBox>
      ) : (
        <Box sx={{ minWidth: '215px' }}>
          <StyledTitle>
            Do Not Disturb for...
            <StyledTooltip
              arrow
              title='Do Not Disturb will pause all notifications from the RevenueWell application'
              placement='top'
            >
              <InfoOutlined />
            </StyledTooltip>
          </StyledTitle>
          <MenuItem onClick={handleSnooze(+new Date() + 1800000)}>30 minutes</MenuItem>
          <MenuItem onClick={handleSnooze(+new Date() + 3600000)}>1 hour</MenuItem>
          <MenuItem onClick={handleSnooze(+new Date() + 7200000)}>2 hours</MenuItem>
          <MenuItem onClick={handleSnooze(+new Date() + 86400000)}>until tomorrow</MenuItem>
        </Box>
      )}
    </StyledMenu>
  );
}
